import { useChainId, useChains, useSwitchChain } from "wagmi";
import { getChain } from "../utils/currencies";

/**
 * Hook to check and switch networks using Wagmi v2 API
 * @returns {Object} Contains switchNetwork function and loading state
 */
const useCheckNetwork = () => {
  // Get current chain ID (Wagmi v2 uses useChainId instead of useNetwork)
  const chainId = useChainId();
  
  // Get available chains from config
  const chains = useChains();

  // Use the useSwitchChain hook from wagmi v2 (renamed from useSwitchNetwork)
  const {
    switchChainAsync,
    isPending: switchNetworkLoading,
  } = useSwitchChain();

  /**
   * Switch to a different blockchain network
   * @param {string} blockchain - The network identifier to switch to (e.g., "sepolia", "matic", "bnb")
   * @param {Function} onSwitchNetwork - Optional callback to run before switching
   * @returns {Promise<{isSwitched: boolean}>} Whether the network was switched
   */
  const switchNetwork = async (blockchain, onSwitchNetwork = null) => {
    try {
      // Convert the target network to lowercase for comparison
      const targetNetwork = blockchain?.toLowerCase();
      
      // Use getChain to find the target chain ID from the network identifier
      // This properly maps network identifiers like "sepolia", "matic", "bnb" to chain IDs
      const targetChainInfo = getChain({ network: targetNetwork });
      const targetChainId = targetChainInfo?.id;

      // If the target chain is not the same as the current chain
      if (targetChainId && targetChainId !== chainId) {
        // If a callback function was provided, call it
        if (onSwitchNetwork) onSwitchNetwork();

        // Find the wagmi chain that matches the target chain ID
        const foundChain = chains?.find((item) => item?.id === targetChainId);

        // If a matching chain was found, switch to it
        if (foundChain) {
          await switchChainAsync({ chainId: foundChain.id });
        } else {
          // Fallback: try to switch directly by chain ID
          await switchChainAsync({ chainId: targetChainId });
        }

        return { isSwitched: true };
      }
      
      return { isSwitched: false };
    } catch (error) {
      console.error("Network switch error:", error);
      throw error;
    }
  };

  /**
   * Switch to a network by chain ID
   * @param {number} targetChainId - The chain ID to switch to
   * @returns {Promise<void>}
   */
  const switchToChainId = async (targetChainId) => {
    if (chainId !== targetChainId) {
      await switchChainAsync({ chainId: targetChainId });
    }
  };

  return { 
    switchNetwork, 
    switchToChainId,
    switchNetworkLoading,
    currentChainId: chainId,
    chains,
  };
};

export default useCheckNetwork;
